import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as FileIcon } from '../../icons/file-05.svg';
import { HandbookUploadField, uploadFieldProblem } from './HandbookUploadField';
import type { HandbookDocument, HandbookPageFill } from './handbookContract';
import { fillErrorMessage } from './handbookFills';

export type PullSource = { documentId: string } | { file: File };

export interface HandbookDocumentPullProps {
  /** The page title: only the parts of the document about it are used */
  title: string;
  disabled?: boolean;
  loadDocuments: () => Promise<HandbookDocument[]>;
  pull: (title: string, source: PullSource) => Promise<HandbookPageFill | null>;
  onPulled: (fill: HandbookPageFill) => void;
}

const NEW = 'new';

/** Documents whose text can still be read from; expired or unreadable ones are not offered. */
const usable = (doc: HandbookDocument) =>
  doc.status === 'extracted' || doc.status === 'pending';

/**
 * "Pull content from a document": pick a document already uploaded to the
 * Handbook, or upload one here (inline, not a second dialog), and write the
 * page from the parts of it about the page's title.
 */
export const HandbookDocumentPull: FC<HandbookDocumentPullProps> = ({
  title,
  disabled = false,
  loadDocuments,
  pull,
  onPulled,
}) => {
  const [documents, setDocuments] = useState<HandbookDocument[] | null>(null);
  const [choice, setChoice] = useState<string>(NEW);
  const [files, setFiles] = useState<File[]>([]);
  const [pulling, setPulling] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadDocuments()
      .then((all) => {
        if (cancelled) return;
        const docs = all.filter(usable);
        setDocuments(docs);
        if (docs.length) setChoice(docs[0].id);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loadDocuments]);

  const trimmed = title.trim();
  const fileProblem = choice === NEW ? uploadFieldProblem(files) : null;
  const ready =
    !!trimmed &&
    !pulling &&
    !fileProblem &&
    (choice !== NEW || files.length > 0);

  const handlePull = async () => {
    if (!ready) return;
    setPulling(true);
    setMessage(null);
    try {
      const fill = await pull(
        trimmed,
        choice === NEW ? { file: files[0] } : { documentId: choice },
      );
      if (fill) onPulled(fill);
      else
        setMessage(
          `We couldn’t find anything about “${trimmed}” in that document. Try another, or write it yourself.`,
        );
    } catch (err) {
      setMessage(
        fillErrorMessage(err, 'Could not read that document. Try again.'),
      );
    } finally {
      setPulling(false);
    }
  };

  return (
    <div className='handbook-pull' data-testid='handbook-pull'>
      <span className='field-label'>Choose a document</span>
      {documents === null ? (
        <p className='handbook-meta'>Loading your documents…</p>
      ) : (
        <div className='handbook-pull-options' role='radiogroup'>
          {documents.map((doc) => (
            <label
              key={doc.id}
              className={`handbook-pull-option${choice === doc.id ? ' selected' : ''}`}
            >
              <input
                type='radio'
                name='handbook-pull-document'
                checked={choice === doc.id}
                disabled={disabled || pulling}
                onChange={() => setChoice(doc.id)}
              />
              <FileIcon aria-hidden='true' />
              <span className='handbook-pull-option-title'>{doc.title}</span>
            </label>
          ))}
          <label
            className={`handbook-pull-option${choice === NEW ? ' selected' : ''}`}
          >
            <input
              type='radio'
              name='handbook-pull-document'
              checked={choice === NEW}
              disabled={disabled || pulling}
              onChange={() => setChoice(NEW)}
            />
            <span className='handbook-pull-option-title'>
              Upload a new document
            </span>
          </label>
        </div>
      )}
      {choice === NEW && (
        <HandbookUploadField
          files={files}
          onChange={(next) => setFiles(next.slice(-1))}
          disabled={disabled || pulling}
          label='New document'
        />
      )}
      <p className='handbook-meta handbook-pull-note'>
        {trimmed
          ? `Only the parts about “${trimmed}” are used. Anything else in the document is left out.`
          : 'Add a title first, so we know what to look for.'}
      </p>
      {message && (
        <p className='organisation-feedback error' role='alert'>
          {message}
        </p>
      )}
      <div>
        <button
          type='button'
          className='handbook-pill primary small'
          disabled={disabled || !ready}
          onClick={handlePull}
        >
          {pulling ? 'Pulling content…' : 'Pull content'}
        </button>
      </div>
    </div>
  );
};
